import { DefaultButton, Dialog, DialogFooter, DialogType, Label, MessageBar, MessageBarType, PrimaryButton, Stack, TextField } from "@fluentui/react"
import { useState } from "react"
import { addQualifyingEvent, getCurrentUserEmail, maskAsDate, safeParseDateTime } from "../lib/helper"
import { makeCRMApiCall } from "../services/apiService"
import { IEnrollmentRecord } from "../types/IEnrollmentRecord"

interface TermMemberDialogProps {
	closePanel: (reload: boolean, success: boolean) => void
	employeeHouseholdMember: IEnrollmentRecord
	householdMembers: IEnrollmentRecord[]
}

export function TermMemberDialog({ closePanel, employeeHouseholdMember, householdMembers }: TermMemberDialogProps) {
	const [isTerming, setTerming] = useState(false)
	const [termDate, setTermDate] = useState<Date | undefined>(undefined)
	const [dateDisplay, setDateDisplay] = useState<string>("")
	const [error, setError] = useState<string | undefined>(undefined)

	const householdMembersWithoutEmployee = householdMembers.filter(h => h.cw_householdmembersid !== employeeHouseholdMember.cw_householdmembersid)

	const [dateError, setDateError] = useState<string | undefined>()
	const onDateChange = (newValue?: string) => {
		const masked = maskAsDate(newValue || "")
		setDateDisplay(masked)

		setDateError(undefined)

		if (masked.length === 10) {
			const parsed = safeParseDateTime(masked)

			if (parsed?.isValid) {
				setTermDate(parsed.toJSDate())
			} else {
				setDateError("Please enter a valid date.")
			}
		}
	}

	return (
		<Dialog
			onDismiss={() => closePanel(false, false)}
			hidden={false}
			dialogContentProps={{
				type: DialogType.normal,
				title: (
					<Stack horizontalAlign="center">
						<h3 style={{ marginTop: 0 }}>Terminate Employee Coverage</h3>
					</Stack>
				)
			}}
			modalProps={{
				isBlocking: true
			}}>
			<Stack tokens={{ childrenGap: 8 }}>
				<div style={{ fontSize: 18, fontWeight: 500 }}>Set the insurance end date for the following:</div>
				<Stack tokens={{ childrenGap: 8 }}>
					<Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
						<Label>Name</Label>
						<span>
							{employeeHouseholdMember.firstName} {employeeHouseholdMember.lastName}
						</span>
					</Stack>
					{householdMembers.length > 1 && (
						<Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="start">
							<Label>Household Members</Label>
							<span>{householdMembersWithoutEmployee.map(h => `${h.firstName} ${h.lastName}`).join(", ")}</span>
						</Stack>
					)}
					<TextField
						label="Insurance End Date"
						placeholder="MM/DD/YYYY"
						inputMode="numeric"
						value={dateDisplay}
						onChange={(_, newValue) => {
							onDateChange(newValue)
						}}
						maxLength={10}
						errorMessage={dateError}
					/>
				</Stack>
			</Stack>
			<DialogFooter>
				<Stack>
					<Stack horizontalAlign="center" horizontal tokens={{ childrenGap: 12 }}>
						<PrimaryButton
							disabled={isTerming}
							onClick={async () => {
								setTerming(true)
								const body = {
									cw_insuranceenddate: termDate?.toISOString().split("T")[0]
								}
								const contactEmail = await getCurrentUserEmail()
								try {
									await Promise.all(householdMembers.map(h => makeCRMApiCall(`cw_householdmemberses(${h.cw_householdmembersid})`, "PATCH", JSON.stringify(body))))
									await Promise.all(
										householdMembers.map(h => {
											return addQualifyingEvent(contactEmail, `Termed household member for ${h.firstName} ${h.lastName}: ${termDate?.toLocaleDateString("en-US")}`, h.employee_id)
										})
									)
									closePanel(true, true)
								} catch (err) {
									console.error(err)
									setError("An error occurred while setting the term date the employee. Please try again.")
								}
							}}
							text="Term"
						/>
						<DefaultButton onClick={() => closePanel(false, false)} text="Cancel" />
					</Stack>
				</Stack>
				{error && <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>}
			</DialogFooter>
		</Dialog>
	)
}
